import type { Difficulty, Puzzle, PuzzleSummary } from "../../shared/nonogram/types";

type PuzzleResponse = { puzzle: Puzzle };
type DailyResponse = PuzzleResponse & { date: string; nextPuzzleAt: string };

type Prefetch = { url: string; promise: Promise<unknown> };

declare global {
  interface Window {
    __nonogramPrefetch?: Prefetch;
  }
}

/**
 * boot.js starts this page's puzzle request before the bundle has finished loading.
 * The first component to ask for that exact URL gets the in-flight promise; every
 * later call (a "play another", a retry after an error) fetches normally, because the
 * prefetch answered one specific question and answering it twice would serve a stale
 * grid.
 */
function takePrefetched<T>(url: string): Promise<T> | undefined {
  const pending = window.__nonogramPrefetch;
  if (!pending || pending.url !== url) return undefined;
  window.__nonogramPrefetch = undefined;
  return pending.promise as Promise<T>;
}

async function requestJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(path, { signal, cache: "no-store", headers: { Accept: "application/json" } });
  if (!response.ok) {
    const fallback = `The puzzle service returned ${response.status}.`;
    const body = (await response.json().catch(() => ({ error: fallback }))) as { error?: string };
    throw new Error(body.error ?? fallback);
  }
  return response.json() as Promise<T>;
}

export function fetchDailyPuzzle(signal?: AbortSignal, date?: string): Promise<DailyResponse> {
  const query = date ? `?date=${encodeURIComponent(date)}` : "";
  const path = `/api/puzzles/daily${query}`;
  return takePrefetched<DailyResponse>(path) ?? requestJson<DailyResponse>(path, signal);
}

export function randomPuzzlePath(
  difficulty: Difficulty,
  excludeIds: readonly string[] = [],
  size?: Puzzle["size"],
): string {
  const ids = [...new Set(excludeIds.filter(Boolean))].sort();
  const exclude = ids.length > 0 ? `&exclude=${encodeURIComponent(ids.join(","))}` : "";
  const sizeQuery = size ? `&size=${size}` : "";
  return `/api/puzzles/random?difficulty=${difficulty}${sizeQuery}${exclude}`;
}

export function fetchRandomPuzzle(
  difficulty: Difficulty,
  signal?: AbortSignal,
  excludeIds: readonly string[] = [],
  size?: Puzzle["size"],
): Promise<PuzzleResponse> {
  const path = randomPuzzlePath(difficulty, excludeIds, size);
  return takePrefetched<PuzzleResponse>(path) ?? requestJson<PuzzleResponse>(path, signal);
}

export function fetchPuzzleById(id: string, signal?: AbortSignal): Promise<PuzzleResponse> {
  return requestJson<PuzzleResponse>(`/api/puzzles/${encodeURIComponent(id)}`, signal);
}

export function fetchPuzzleSummaries(signal?: AbortSignal): Promise<{ puzzles: PuzzleSummary[] }> {
  return requestJson<{ puzzles: PuzzleSummary[] }>("/api/puzzles", signal);
}

/** Only the grids a player has already solved: everything else stays a locked tile. */
export function fetchSolvedPuzzles(ids: readonly string[], signal?: AbortSignal): Promise<{ puzzles: Puzzle[] }> {
  if (ids.length === 0) return Promise.resolve({ puzzles: [] });
  return requestJson<{ puzzles: Puzzle[] }>(`/api/puzzles?ids=${encodeURIComponent(ids.join(","))}`, signal);
}
